import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Upload, Video, Sparkles, FileText, ArrowRight, Clock, CheckCircle2, Loader2, AlertCircle,
  LayoutGrid, List
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const statusConfig = {
  processing: { icon: Loader2, label: 'Processing', color: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20', animate: true },
  extracting_audio: { icon: Loader2, label: 'Extracting Audio', color: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20', animate: true },
  transcribing: { icon: Loader2, label: 'Transcribing', color: 'bg-blue-500/10 text-blue-400 border-blue-500/20', animate: true },
  ready: { icon: CheckCircle2, label: 'Ready', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
  failed: { icon: AlertCircle, label: 'Failed', color: 'bg-red-500/10 text-red-400 border-red-500/20' },
};

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [genView, setGenView] = useState('grid');
  const navigate = useNavigate();

  useEffect(() => {
    loadStats();
    const interval = setInterval(loadStats, 5000);
    return () => clearInterval(interval);
  }, []);

  const loadStats = async () => {
    try {
      const res = await dashboardAPI.stats();
      setStats(res.data);
    } catch (err) {
      if (loading) toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatSize = (bytes) => {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8 p-8" data-testid="dashboard-page">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="bg-zinc-900/60 border-white/5 hover:border-primary/30 transition-all duration-300 hover:-translate-y-1">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground font-medium">Total Videos</p>
                <p className="text-3xl font-heading font-bold mt-1">{stats?.total_videos || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-indigo-500/10 flex items-center justify-center">
                <Video className="w-6 h-6 text-indigo-400" strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/60 border-white/5 hover:border-primary/30 transition-all duration-300 hover:-translate-y-1">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground font-medium">Generations</p>
                <p className="text-3xl font-heading font-bold mt-1">{stats?.total_generations || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-purple-400" strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/60 border-white/5 hover:border-primary/30 transition-all duration-300 hover:-translate-y-1">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground font-medium">Ready</p>
                <p className="text-3xl font-heading font-bold mt-1">{stats?.ready_videos || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-emerald-400" strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/60 border-white/5 hover:border-primary/30 transition-all duration-300 hover:-translate-y-1">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground font-medium">Processing</p>
                <p className="text-3xl font-heading font-bold mt-1">{stats?.processing_videos || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-yellow-500/10 flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-400" strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Upload CTA + Recent */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Upload CTA */}
        <Card
          className="lg:col-span-1 bg-gradient-to-br from-indigo-500/10 via-zinc-900/60 to-purple-500/10 border-primary/20 hover:border-primary/40 transition-all duration-300 cursor-pointer group"
          onClick={() => navigate('/upload')}
          data-testid="dashboard-upload-cta"
        >
          <CardContent className="p-8 flex flex-col items-center justify-center text-center h-full min-h-[240px]">
            <div className="w-16 h-16 rounded-2xl bg-primary/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
              <Upload className="w-8 h-8 text-primary" strokeWidth={1.5} />
            </div>
            <h3 className="font-heading text-xl font-semibold mb-2">Upload New Video</h3>
            <p className="text-sm text-muted-foreground mb-4">Drop a clip and generate scroll-stopping social copy in seconds</p>
            <Button className="rounded-full bg-primary hover:bg-primary/90 shadow-lg shadow-indigo-500/25 transition-all duration-300 hover:scale-[1.02]" data-testid="dashboard-upload-button">
              Get Started <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </CardContent>
        </Card>

        {/* Recent Videos */}
        <Card className="lg:col-span-2 bg-zinc-900/60 border-white/5">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg">Recent Uploads</CardTitle>
              {stats?.recent_videos?.length > 0 && (
                <Button variant="ghost" size="sm" onClick={() => navigate('/library')} className="text-muted-foreground hover:text-primary" data-testid="view-all-videos">
                  View All <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {!stats?.recent_videos?.length ? (
              <div className="flex flex-col items-center justify-center py-12 text-center" data-testid="empty-videos">
                <FileText className="w-10 h-10 text-muted-foreground/40 mb-3" strokeWidth={1.5} />
                <p className="text-sm text-muted-foreground">No videos uploaded yet</p>
                <p className="text-xs text-muted-foreground/60 mt-1">Upload your first clip to get started</p>
              </div>
            ) : (
              <div className="space-y-3">
                {stats.recent_videos.map((video) => {
                  const sc = statusConfig[video.status] || statusConfig.processing;
                  const StatusIcon = sc.icon;
                  return (
                    <div
                      key={video.id}
                      className="flex items-center justify-between p-4 rounded-xl bg-secondary/30 border border-white/5 hover:border-white/10 transition-all duration-200 cursor-pointer group"
                      onClick={() => video.status === 'ready' ? navigate(`/video/${video.id}`) : null}
                      data-testid={`video-item-${video.id}`}
                    >
                      <div className="flex items-center gap-4 min-w-0">
                        <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center shrink-0">
                          <Video className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{video.filename}</p>
                          <p className="text-xs text-muted-foreground">{formatSize(video.file_size)} &middot; {formatDate(video.created_at)}</p>
                        </div>
                      </div>
                      <Badge variant="outline" className={`${sc.color} shrink-0`}>
                        <StatusIcon className={`w-3 h-3 mr-1 ${sc.animate ? 'animate-spin' : ''}`} />
                        {sc.label}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Generations */}
      {stats?.recent_generations?.length > 0 && (
        <Card className="bg-zinc-900/60 border-white/5">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg">Recent Generations</CardTitle>
              <div className="flex items-center gap-1 bg-secondary/30 rounded-lg p-0.5" data-testid="dashboard-gen-view-toggle">
                <Button
                  variant="ghost" size="sm"
                  className={cn("h-7 w-7 p-0", genView === 'grid' && "bg-primary/20 text-primary")}
                  onClick={() => setGenView('grid')}
                  data-testid="dashboard-gen-view-grid"
                >
                  <LayoutGrid className="w-3.5 h-3.5" />
                </Button>
                <Button
                  variant="ghost" size="sm"
                  className={cn("h-7 w-7 p-0", genView === 'list' && "bg-primary/20 text-primary")}
                  onClick={() => setGenView('list')}
                  data-testid="dashboard-gen-view-list"
                >
                  <List className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {genView === 'grid' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {stats.recent_generations.slice(0, 6).map((gen) => (
                  <div
                    key={gen.id}
                    className="p-4 rounded-xl bg-secondary/30 border border-white/5 hover:border-primary/20 transition-all duration-200 space-y-2 cursor-pointer hover:-translate-y-0.5"
                    onClick={() => navigate(`/generate/${gen.video_id}/${gen.preset_id}`)}
                    data-testid={`gen-item-${gen.id}`}
                  >
                    <p className="font-medium text-sm line-clamp-1">{gen.title}</p>
                    <p className="text-xs text-muted-foreground line-clamp-2">{gen.description}</p>
                    <div className="flex gap-1 flex-wrap">
                      {gen.hashtags?.slice(0, 3).map((h, i) => (
                        <Badge key={i} variant="secondary" className="text-[10px] bg-primary/10 text-primary border-0">
                          #{h.replace('#', '')}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-1.5">
                {stats.recent_generations.slice(0, 6).map((gen) => (
                  <div
                    key={gen.id}
                    className="flex items-center gap-4 p-3 rounded-xl bg-secondary/20 border border-white/5 hover:border-primary/20 transition-all duration-200 cursor-pointer group"
                    onClick={() => navigate(`/generate/${gen.video_id}/${gen.preset_id}`)}
                    data-testid={`gen-list-item-${gen.id}`}
                  >
                    <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <Sparkles className="w-4 h-4 text-primary" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-heading font-semibold text-sm truncate group-hover:text-primary transition-colors">{gen.title}</p>
                      <p className="text-xs text-muted-foreground truncate mt-0.5">{gen.description}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {gen.hashtags?.slice(0, 2).map((h, i) => (
                        <Badge key={i} variant="secondary" className="text-[9px] bg-primary/10 text-primary border-0 hidden lg:inline-flex">
                          #{h.replace('#', '')}
                        </Badge>
                      ))}
                      <span className="text-[10px] text-muted-foreground/40 w-16 text-right">{formatDate(gen.created_at).split(',')[0]}</span>
                      <ArrowRight className="w-3.5 h-3.5 text-muted-foreground/30 group-hover:text-primary transition-colors" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
